import {
  addWrapper,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
  addRef,
} from 'ad-hok'
import React, { ComponentProps, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { useDropzone } from 'react-dropzone'
import { addApi } from 'shared/src/api'
import {
  addDataLoadingFromPromise,
  isError,
  isLoading,
} from 'shared/src/utils/dataLoading'
import { addPropTrackingRef, branchIfPassesPredicate } from 'ad-hok-utils'
import Hls from 'hls.js'
import {
  MediaContent,
  Image as ApiImage,
  Video as ApiVideo,
  isImageStateError,
  isImageStateProcessing,
  isVideoStateProcessing,
  isVideoStateError,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { fontFamily, textColorLight } from './styles'
import { Field } from './Field'
import { Button } from './Button'
import { TextField } from './TextField'
import shareIcon from '../assets/share-icon.svg'
import { ShareMediaModal } from './myRodeo/ShareMediaModal'

export const IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
]
export const VIDEO_CONTENT_TYPES = ['video/mp4', 'video/quicktime']

const INSTAGRAM_VIDEO_REGEX = /^https:\/\/.+\.cdninstagram.com\/.+\.mp4.*/
const TIK_TOK_VIDEO_REGEX = /^https:\/\/.+\.tiktokcdn.*.com\/.+\/video.+/

const VIDEO_REGEXES = [INSTAGRAM_VIDEO_REGEX, TIK_TOK_VIDEO_REGEX]

const useStyles = createUseStyles({
  drop: {
    width: 'calc(100% - 4px)',
    height: 100,
    border: '2px dashed rgba(0, 0, 0, 0.17)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: textColorLight,
    fontFamily,
    fontSize: 14,
    marginBottom: 10,
  },
  or: {
    marginBottom: 10,
    color: textColorLight,
    fontSize: 14,
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
  },
  image: {
    maxHeight: 400,
    maxWidth: '100%',
    marginBottom: 20,
  },
  imageStatus: {
    fontFamily,
    fontSize: 14,
    color: textColorLight,
  },
})

type UploaderState = 'waiting' | 'uploading' | 'uploadError'

interface UploaderProps {
  onUploadedImage: (image: ApiImage) => void
  onUploadedVideo?: (video: ApiVideo) => void
  suggestion?: string | number | null
}

const Uploader: FC<UploaderProps> = flowMax(
  addDisplayName('Uploader'),
  addApi,
  addState('url', 'setUrl', ''),
  addState('state', 'setState', typedAs<UploaderState>('waiting')),
  addState(
    'abortController',
    'setAbortController',
    typedAs<AbortController | null>(null)
  ),
  addHandlers({
    uploadImage:
      ({ api, setState, onUploadedImage, setAbortController }) =>
      async (file: File) => {
        setState('uploading')

        const abortController = new AbortController()
        setAbortController(abortController)

        try {
          const { presignedUrl, key } = await api.images.presignedUrl({
            filename: file.name,
          })
          await fetch(decodeURIComponent(presignedUrl), {
            method: 'PUT',
            body: file,
            signal: abortController.signal,
          })
          const image = await api.images.create({
            type: 's3',
            value: key,
          })

          onUploadedImage(image)
          setState('waiting')
        } catch (error) {
          console.error('Error uploading file', error)

          if (error instanceof DOMException && error.name === 'AbortError') {
            setState('waiting')
          } else {
            setState('uploadError')
          }
        }
      },
    uploadVideo:
      ({ api, setState, onUploadedVideo, setAbortController }) =>
      async (file: File) => {
        setState('uploading')

        const abortController = new AbortController()
        setAbortController(abortController)

        try {
          const { presignedUrl, id } = await api.videos.presignedUrl()
          await fetch(decodeURIComponent(presignedUrl), {
            method: 'PUT',
            body: file,
            signal: abortController.signal,
          })

          const { playbackId } = await api.videos.playbackId(id)

          const video = await api.videos.create({
            type: 'mux',
            value: playbackId,
          })

          onUploadedVideo!(video)
          setState('waiting')
        } catch (error) {
          console.error('Error uploading file to Mux', error)

          if (error instanceof DOMException && error.name === 'AbortError') {
            setState('waiting')
          } else {
            setState('uploadError')
          }
        }
      },
  }),
  addHandlers({
    uploadContentFromUrl:
      ({ api, setState, onUploadedImage, onUploadedVideo }) =>
      async (url: string) => {
        setState('uploading')

        try {
          if (
            onUploadedVideo &&
            VIDEO_REGEXES.some((regex) => regex.test(url))
          ) {
            const video = await api.videos.create({
              type: 'url',
              value: url,
            })

            onUploadedVideo(video)
          } else {
            const image = await api.images.create({
              type: 'url',
              value: url,
            })

            onUploadedImage(image)
          }
          setState('waiting')
        } catch (error) {
          console.error('Error creating image', error)
          setState('uploadError')
        }
      },
  }),
  addHandlers({
    onChangeUrl:
      ({ setUrl }) =>
      (value: string) => {
        setUrl(value)
      },
    onUrlKeyPress:
      ({ uploadContentFromUrl, url }) =>
      async (event: React.KeyboardEvent<HTMLInputElement>) => {
        console.log(event)
        if (event.key !== 'Enter') {
          return
        }

        uploadContentFromUrl(url)
      },
    onDrop:
      ({ uploadImage, uploadVideo }) =>
      async (files: File[]) => {
        if (files.length !== 1) {
          return
        }

        const [file] = files

        if (VIDEO_CONTENT_TYPES.includes(file.type)) {
          await uploadVideo(file)
        } else {
          await uploadImage(file)
        }
      },
    onCancel:
      ({ abortController }) =>
      () => {
        abortController?.abort()
      },
  }),
  addProps(({ state }) => ({
    disabled: state === 'uploading',
  })),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addEffect(
    ({ suggestion, uploadContentFromUrl }) =>
      () => {
        if (suggestion) {
          uploadContentFromUrl(`${suggestion}`)
        }
      },
    ['suggestion']
  ),
  ({
    onChangeUrl,
    onUrlKeyPress,
    onDrop,
    url,
    state,
    disabled,
    onUploadedVideo,
    onCancel,
    t,
    classes,
    suggestion,
  }) => {
    const { getRootProps, getInputProps } = useDropzone({
      disabled,
      accept: (onUploadedVideo
        ? [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES]
        : IMAGE_CONTENT_TYPES
      ).join(','),
      onDrop,
    })

    return (
      <div>
        <div {...getRootProps()} className={classes.drop}>
          <input {...getInputProps()} />
          {state === 'waiting' && (
            <p>{t('admin.resources.images.uploadImage')}</p>
          )}
          {state === 'uploading' && (
            <p>{t('admin.resources.images.uploading')}</p>
          )}
          {state === 'uploadError' && (
            <p>{t('admin.resources.images.uploadError')}</p>
          )}
        </div>
        {state === 'uploading' && (
          <Button
            text={t('admin.resources.general.cancel')}
            onClick={onCancel}
          />
        )}
        <p className={classes.or}>{t('admin.resources.images.or')}</p>
        <TextField
          id="productImageUrl"
          suggestion={suggestion}
          value={url}
          onChange={onChangeUrl}
          onKeyPress={onUrlKeyPress}
          disabled={disabled}
          placeholder={t('admin.resources.images.url')}
        />
      </div>
    )
  }
)

interface ImageProps {
  id: number
  onDelete: () => void
  showShare?: boolean
}

const Image: FC<ImageProps> = flowMax(
  addDisplayName('Image'),
  addApi,
  addDataLoadingFromPromise('image', ({ id, api }) => api.images.get(id), [
    'id',
  ]),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addWrapper((render, { onDelete, classes, t }) => (
    <div className={classes.imageContainer}>
      {render()}
      <Button text={t('admin.resources.general.delete')} onClick={onDelete} />
    </div>
  )),
  branchIfPassesPredicate('image', isLoading, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.general.loading')}
      </p>
    ),
  }),
  branchIfPassesPredicate('image', isError, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.general.loadingError')}
      </p>
    ),
  }),
  addProps(({ image }) => ({
    state: image.data.state,
  })),
  addEffect(
    ({ state, reloadImage }) =>
      () => {
        if (!isImageStateProcessing(state)) {
          return
        }

        const timer = setInterval(() => {
          reloadImage()
        }, 1000)

        return () => {
          clearInterval(timer)
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isImageStateProcessing, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.images.processing')}
      </p>
    ),
  }),
  branchIfPassesPredicate('state', isImageStateError, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.images.processingError')}
      </p>
    ),
  }),
  addState('shareMediaModalOpen', 'setShareMediaModalOpen', false),
  ({
    state,
    setShareMediaModalOpen,
    shareMediaModalOpen,
    showShare,
    classes,
  }) => (
    <>
      {showShare && (
        <button onClick={() => setShareMediaModalOpen(true)}>
          <img src={shareIcon} alt="" />
        </button>
      )}
      {shareMediaModalOpen && showShare && (
        <ShareMediaModal
          displayText={'rodeo'}
          imageUrl={state.value.full}
          onClose={() => setShareMediaModalOpen(false)}
        />
      )}
      <img src={state.value.full} className={classes.image} alt="" />
    </>
  )
)

interface VideoProps {
  id: number
  onDelete: () => void
  showShare?: boolean
}

const Video: FC<VideoProps> = flowMax(
  addDisplayName('Video'),
  addApi,
  addDataLoadingFromPromise('video', ({ id, api }) => api.videos.get(id), [
    'id',
  ]),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addWrapper((render, { onDelete, classes, t }) => (
    <div className={classes.imageContainer}>
      {render()}
      <Button text={t('admin.resources.general.delete')} onClick={onDelete} />
    </div>
  )),
  branchIfPassesPredicate('video', isLoading, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.general.loading')}
      </p>
    ),
  }),
  branchIfPassesPredicate('video', isError, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.general.loadingError')}
      </p>
    ),
  }),
  addProps(({ video }) => ({
    state: video.data.state,
  })),
  addEffect(
    ({ state, reloadVideo }) =>
      () => {
        if (!isVideoStateProcessing(state)) {
          return
        }

        const timer = setInterval(() => {
          reloadVideo()
        }, 1000)

        return () => {
          clearInterval(timer)
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isVideoStateProcessing, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.images.processing')}
      </p>
    ),
  }),
  branchIfPassesPredicate('state', isVideoStateError, {
    returns: ({ t, classes }) => (
      <p className={classes.imageStatus}>
        {t('admin.resources.images.processingError')}
      </p>
    ),
  }),
  addProps(({ state }) => ({
    videoUrl: `https://stream.mux.com/${state.value}.m3u8`,
    thumbnailUrl: `https://image.mux.com/${state.value}/thumbnail.jpg`,
  })),
  addRef('ref', typedAs<HTMLVideoElement | null>(null)),
  addEffect(
    ({ videoUrl, ref }) =>
      () => {
        let hls: Hls | null = null

        if (!ref.current) {
          return
        }

        const video = ref.current

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = videoUrl
        } else if (Hls.isSupported()) {
          hls = new Hls()
          hls.loadSource(videoUrl)
          hls.attachMedia(video)
        } else {
          console.error("This is a legacy browser that doesn't support MSE")
        }

        return () => {
          hls?.destroy()
          video.onplaying = null
        }
      },
    ['videoUrl', 'ref', 'setVideoElementPlaying']
  ),
  addState('shareMediaModalOpen', 'setShareMediaModalOpen', false),
  ({
    thumbnailUrl,
    ref,
    shareMediaModalOpen,
    setShareMediaModalOpen,
    showShare,
    classes,
  }) => (
    <>
      {showShare && (
        <button onClick={() => setShareMediaModalOpen(true)}>
          <img src={shareIcon} alt="" />
        </button>
      )}
      {shareMediaModalOpen && showShare && (
        <ShareMediaModal
          displayText={'rodeo'}
          imageUrl={thumbnailUrl}
          onClose={() => setShareMediaModalOpen(false)}
        />
      )}
      <video
        controls
        playsInline
        loop
        muted
        poster={thumbnailUrl}
        ref={ref}
        className={classes.image}
      />
    </>
  )
)

interface Props extends ComponentProps<typeof Field> {
  value: number | null | undefined
  onChange: (id: number | undefined) => void
}

export const ImageField: FC<Props> = flowMax(
  addDisplayName('ImageField'),
  addPropTrackingRef('onChange', 'onChangeRef'),
  addHandlers({
    onUploaded:
      ({ onChangeRef }) =>
      (image: ApiImage) => {
        onChangeRef.current(image.id)
      },
    onDelete:
      ({ onChangeRef }) =>
      () => {
        onChangeRef.current(undefined)
      },
  }),
  ({ id, label, error, value, onUploaded, onDelete, suggestion }) => (
    <Field id={id} label={label} error={error}>
      {value ? (
        <Image id={value} onDelete={onDelete} />
      ) : (
        <Uploader onUploadedImage={onUploaded} suggestion={suggestion} />
      )}
    </Field>
  )
)

interface ContentFieldProps extends ComponentProps<typeof Field> {
  value: MediaContent | undefined
  showShare?: boolean
  onChange: (content: MediaContent | undefined) => void
}

export const ContentField: FC<ContentFieldProps> = flowMax(
  addDisplayName('ContentField'),
  addPropTrackingRef('onChange', 'onChangeRef'),
  addHandlers({
    onUploadedImage:
      ({ onChangeRef }) =>
      (image: ApiImage) => {
        onChangeRef.current({ type: 'image', value: image.id })
      },
    onUploadedVideo:
      ({ onChangeRef }) =>
      (video: ApiVideo) => {
        onChangeRef.current({ type: 'video', value: video.id })
      },
    onDelete:
      ({ onChangeRef }) =>
      () => {
        onChangeRef.current(undefined)
      },
  }),
  ({
    id,
    label,
    error,
    value,
    onUploadedImage,
    onUploadedVideo,
    onDelete,
    suggestion,
    showShare,
  }) => (
    <Field id={id} label={label} error={error} suggestion={suggestion}>
      {value ? (
        value.type === 'image' ? (
          <Image id={value.value} onDelete={onDelete} showShare={showShare} />
        ) : (
          <Video id={value.value} onDelete={onDelete} showShare={showShare} />
        )
      ) : (
        <Uploader
          onUploadedImage={onUploadedImage}
          onUploadedVideo={onUploadedVideo}
          suggestion={suggestion}
        />
      )}
    </Field>
  )
)
